import React from 'react';
import { Button } from '@/components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { HelpCircle, MessageCircle } from 'lucide-react';

const FAQSection = () => {
  return (
    <section id="faq" className="py-20 lg:py-28 bg-background">
      <div className="section-container">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Left Column - Header */}
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-32">
              <span className="inline-block text-sm font-semibold text-accent uppercase tracking-wider mb-4">
                Got Questions?
              </span>
              <h3 className="font-heading text-2xl sm:text-3xl font-bold text-foreground mb-6">
                Frequently Asked Questions
              </h3>
              <p className="text-muted-foreground leading-relaxed mb-8">
                Find answers to common questions about car finance claims. 
                Cannot find what you are looking for? Get in touch with our team.
              </p>
              
              {/* Contact Card */}
              <div className="p-6 rounded-2xl bg-muted/50 border border-border">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center">
                    <MessageCircle className="w-5 h-5 text-accent" />
                  </div>
                  <div>
                    <h4 className="font-heading font-semibold text-foreground">
                      Still have questions?
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      Use our AI assistant above to get an instant answer
                    </p>
                  </div>
                </div>
                <Button variant="outline" className="w-full" asChild>
                  <a href="#ask-ai">Ask Our AI</a>
                </Button>
              </div>
            </div>
          </div>

          {/* Right Column - FAQ Accordion */}
          <div className="lg:col-span-8">
            <Accordion type="single" collapsible className="space-y-4">
              <AccordionItem
                value="item-0"
                className="bg-card border border-border rounded-xl px-6 data-[state=open]:shadow-md transition-shadow"
              >
                <AccordionTrigger className="text-left text-base font-semibold text-foreground hover:no-underline py-5">
                  <div className="flex items-start gap-4">
                    <HelpCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>What is mis-sold car finance?</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed pl-9 pb-5">
                  Mis-sold car finance typically relates to Discretionary Commission Arrangements (DCAs). 
                  This is where the car dealer could set the interest rate on your finance agreement, and 
                  the higher the rate they set, the more commission they earned. This meant many consumers 
                  paid more for their car finance than they should have, without being told about this 
                  conflict of interest. The FCA banned DCAs in January 2021, and consumers affected between 
                  April 2007 and October 2024 may be entitled to compensation.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem
                value="item-1"
                className="bg-card border border-border rounded-xl px-6 data-[state=open]:shadow-md transition-shadow"
              >
                <AccordionTrigger className="text-left text-base font-semibold text-foreground hover:no-underline py-5">
                  <div className="flex items-start gap-4">
                    <HelpCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>How much is the average claim?</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed pl-9 pb-5">
                  The Financial Conduct Authority (FCA) has indicated that under its Consumer Redress Scheme, 
                  eligible claims could be an average of £830* per finance agreement. However, actual 
                  compensation varies depending on factors such as the interest rate charged, the length of 
                  your agreement, and the total amount financed. The compensation scheme for mis-sold car 
                  finance is expected to cost lenders £7.5 billion in total redress.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem
                value="item-2"
                className="bg-card border border-border rounded-xl px-6 data-[state=open]:shadow-md transition-shadow"
              >
                <AccordionTrigger className="text-left text-base font-semibold text-foreground hover:no-underline py-5">
                  <div className="flex items-start gap-4">
                    <HelpCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>How much does this cost?</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed pl-9 pb-5">
                  We operate on a No Win, No Fee basis. This means you will not pay anything upfront, and 
                  if your claim is unsuccessful, you will not owe us a penny. If we win your claim, our 
                  success fee is a maximum of 36% (inclusive of VAT) of the compensation recovered. 
                  You have a 14-day cooling-off period during which you can cancel without charge. 
                  Please refer to our Terms of Engagement for full details on charges.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem
                value="item-3"
                className="bg-card border border-border rounded-xl px-6 data-[state=open]:shadow-md transition-shadow"
              >
                <AccordionTrigger className="text-left text-base font-semibold text-foreground hover:no-underline py-5">
                  <div className="flex items-start gap-4">
                    <HelpCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>Can I make more than one claim?</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed pl-9 pb-5">
                  Yes! You can make one claim per finance agreement. So if, for example, you took out four 
                  cars on finance between April 2007 and October 2024, then you could be eligible for four 
                  separate claims. Our online tool can help you find ALL of your car finance agreements 
                  from this period.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem
                value="item-4"
                className="bg-card border border-border rounded-xl px-6 data-[state=open]:shadow-md transition-shadow"
              >
                <AccordionTrigger className="text-left text-base font-semibold text-foreground hover:no-underline py-5">
                  <div className="flex items-start gap-4">
                    <HelpCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>What if I do not have my paperwork?</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed pl-9 pb-5">
                  Do not worry - you do not need to have any paperwork to get started. We can help you 
                  find your finance agreements and request the necessary documentation directly from the 
                  lender on your behalf. All you need to begin is your basic personal details.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem
                value="item-5"
                className="bg-card border border-border rounded-xl px-6 data-[state=open]:shadow-md transition-shadow"
              >
                <AccordionTrigger className="text-left text-base font-semibold text-foreground hover:no-underline py-5">
                  <div className="flex items-start gap-4">
                    <HelpCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>Will making a claim affect my credit score?</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed pl-9 pb-5">
                  No, making a claim will not affect your credit score. The claim is against the lender 
                  for hidden commission, not a dispute about your ability to repay the loan. Your credit 
                  history and score remain completely unaffected throughout the claims process.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem
                value="item-6"
                className="bg-card border border-border rounded-xl px-6 data-[state=open]:shadow-md transition-shadow"
              >
                <AccordionTrigger className="text-left text-base font-semibold text-foreground hover:no-underline py-5">
                  <div className="flex items-start gap-4">
                    <HelpCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>Can I claim if I have already paid off my car finance?</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed pl-9 pb-5">
                  Yes, absolutely! You can still make a claim even if you have fully paid off your car 
                  finance. As long as your finance agreement was active between April 2007 and October 2024, 
                  you may be entitled to compensation for hidden commission charges.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem
                value="item-7"
                className="bg-card border border-border rounded-xl px-6 data-[state=open]:shadow-md transition-shadow"
              >
                <AccordionTrigger className="text-left text-base font-semibold text-foreground hover:no-underline py-5">
                  <div className="flex items-start gap-4">
                    <HelpCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>Can I claim back myself?</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed pl-9 pb-5">
                  Yes, you do not have to use a law firm or claims management company to make your claim. 
                  You are entitled to pursue it directly at no cost by complaining to your lender. 
                  If you are dissatisfied with the lenders response, you can escalate the matter to the 
                  Financial Ombudsman Service at no cost. However, many people prefer to use our 
                  expertise to navigate the process and maximise their compensation.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </div>
    </section>
  );
};

export { FAQSection };
